import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'

import { useBaseResetPassword, useBaseUpdatePassword } from '@/api/react-query'
import { Alert } from '@/components/base/alert'
import { Heading } from '@/components/base/heading'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/reset-password')({
	component: ResetPassword,
	validateSearch: z.object({
		token: z.string().optional()
	}),
	staticData: {
		crumb: ({ context: { L } }) => L.ResetPassword.Title()
	}
})

function ResetPassword() {
	const { L } = useI18n()
	const { token } = Route.useSearch()
	const [showAlert, setShowAlert] = useState(false)

	const resetPassword = useBaseResetPassword()

	const form = useAppForm({
		defaultValues: {
			email: ''
		},
		onSubmit: async ({ value, formApi }) => {
			const result = await resetPassword({
				body: {
					email: value.email
				}
			})
			if (result.isSuccess) {
				setShowAlert(true)
				formApi.reset()
			} else {
				toast.error(result.response as any)
			}
		}
	})

	if (token) {
		return <ChooseNewPassword token={token} />
	}

	return (
		<div className='flex min-h-dvh w-dvw items-center justify-center bg-ui-bg-base'>
			<div className='m-4 flex w-full max-w-[300px] flex-col items-center'>
				<div className='mb-4 flex flex-col items-center'>
					<Heading>{L.ResetPassword.Title()}</Heading>
					<Text size='small' className='text-center text-ui-fg-subtle'>
						{L.ResetPassword.Hint()}
					</Text>
				</div>
				<form.AppForm>
					<FormContainer>
						<FormFieldGroup>
							<form.AppField
								name='email'
								validators={{
									onSubmit: ({ value }) => {
										if (value && !z.string().email().safeParse(value).success) {
											return L.ResetPassword.InvalidEmail()
										}
									}
								}}
								children={field => <field.TextField placeholder={L.ResetPassword.Email()} />}
							/>
						</FormFieldGroup>
						{showAlert && (
							<Alert dismissible variant='success'>
								<div className='flex flex-col'>
									<span className='mb-1 text-ui-fg-base'>
										{L.ResetPassword.SuccessfulRequestTitle()}
									</span>
									<span>{L.ResetPassword.SuccessfulRequest()}</span>
								</div>
							</Alert>
						)}
						<form.SaveButton label={L.ResetPassword.SendResetInstructions()} />
					</FormContainer>
				</form.AppForm>
				<span className='txt-small my-6'>
					<Link
						key='login-link'
						to='/login'
						className='hover:text-ui-fg-base-hover focus-visible:text-ui-fg-base-hover text-ui-fg-base outline-none transition-fg'
					>
						{L.ResetPassword.BackToLogin()}
					</Link>
				</span>
			</div>
		</div>
	)
}

const ChooseNewPassword = ({ token }: { token: string }) => {
	const { L } = useI18n()

	const [showAlert, setShowAlert] = useState(false)

	const resetPassword = useBaseUpdatePassword()

	const form = useAppForm({
		defaultValues: {
			password: '',
			repeat_password: ''
		},
		onSubmit: async ({ value }) => {
			if (value.password !== value.repeat_password) {
				toast.error(L.ResetPassword.PasswordMismatch())
				return
			}

			const result = await resetPassword({
				body: {
					token,
					newPassword: value.password
				}
			})
			if (result.isSuccess) {
				setShowAlert(true)
			} else {
				toast.error(result.response as any)
			}
		}
	})

	return (
		<div className='flex min-h-dvh w-dvw items-center justify-center bg-ui-bg-subtle'>
			<div className='m-4 flex w-full max-w-[280px] flex-col items-center'>
				<div className='mb-6 flex flex-col items-center'>
					<Heading>{L.ResetPassword.Title()}</Heading>
					<Text size='small' className='text-center text-ui-fg-subtle'>
						{L.ResetPassword.NewPasswordHint()}
					</Text>
				</div>
				<form.AppForm>
					<FormContainer>
						<FormFieldGroup>
							<form.AppField
								name='password'
								children={field => <field.TextField placeholder={L.ResetPassword.NewPassword()} />}
							/>
							<form.AppField
								name='repeat_password'
								children={field => (
									<field.TextField placeholder={L.ResetPassword.RepeatNewPassword()} />
								)}
							/>
						</FormFieldGroup>
						{showAlert && (
							<Alert dismissible variant='success'>
								<div className='flex flex-col'>
									<span className='mb-1 text-ui-fg-base'>
										{L.ResetPassword.SuccessfulResetTitle()}
									</span>
									<span>{L.ResetPassword.SuccessfulReset()}</span>
								</div>
							</Alert>
						)}
						{!showAlert && <form.SaveButton label={L.ResetPassword.ResetPassword()} />}
					</FormContainer>
				</form.AppForm>
				<span className='txt-small my-6'>
					<Link
						key='login-link'
						to='/login'
						className='hover:text-ui-fg-base-hover focus-visible:text-ui-fg-base-hover text-ui-fg-base outline-none transition-fg'
					>
						{L.ResetPassword.BackToLogin()}
					</Link>
				</span>
			</div>
		</div>
	)
}
